// Feed Service
// Handles feed data fetching and management

import {
  FeedItem,
  FeedOptions,
  FeedFilters
} from "../../types/socialFeed";
import { MockFeedData } from "./mockFeedData";
import { logger } from "../../utils/logger";
import { config } from "../../config/env";
import { authInterceptor } from "../Authentication/authInterceptor";

const API_BASE = `${config.api.baseUrl}/feed`;

function headers(json = true): HeadersInit {
  const h: HeadersInit = { Authorization: `Bearer ${authInterceptor.getAccessToken() || ''}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

function toFeedItem(row: any): FeedItem {
  return {
    id: row.id,
    type: row.type,
    userId: row.userId,
    // Match candidates carry their own identity — MatchCard renders these two
    // directly, so dropping them here is what leaves the card blank.
    username: row.username ?? '',
    avatar: row.avatar ?? '',
    timestamp: new Date(row.timestamp),
    content: row.content,
    engagement: {
      likes: row.engagement?.likes ?? 0,
      comments: row.engagement?.comments ?? 0,
      shares: row.engagement?.shares ?? 0,
      views: row.engagement?.views ?? 0,
      isLiked: false,
      isCommented: false,
      isShared: false,
    },
    metadata: row.metadata ?? {},
  };
}

export class FeedService {
  static async getFeedItems(
    userId: string,
    options: FeedOptions
  ): Promise<FeedItem[]> {
    const { tab, limit = 20, page = 0, seed } = options;

    try {
      const offset = page * limit;
      const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (seed) query.set('seed', seed);
      const response = await fetch(`${API_BASE}/items?${query}`, { headers: headers(false) });
      if (!response.ok) throw new Error(`Failed to fetch feed items: ${response.statusText}`);
      const { data } = await response.json();
      const items = (data ?? []).map(toFeedItem);
      return this.filterByTab(items, tab);
    } catch (error) {
      console.error("Failed to fetch feed items from backend, falling back to mock data:", error);
    }

    try {
      const { filters } = options;
      const allItems = await MockFeedData.getFeedItems(userId, filters as FeedFilters);
      const filteredItems = this.filterByTab(allItems, tab);
      const startIndex = options.page * limit;
      const endIndex = startIndex + limit;
      return filteredItems.slice(startIndex, endIndex);
    } catch (error) {
      console.error("Failed to fetch mock feed items:", error);
      return [];
    }
  }

  static async recordInteraction(interaction: any): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/interactions`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ itemId: interaction.itemId, type: interaction.type, commentText: interaction.commentText }),
      });
      if (!response.ok) throw new Error(`Failed to record interaction: ${response.statusText}`);
      return;
    } catch (error) {
      console.error("Failed to record interaction, falling back to local logging:", error);
    }

    logger.log("Interaction recorded (local only):", interaction);
  }

  private static filterByTab(items: FeedItem[], tab: string): FeedItem[] {
    switch (tab) {
      case "heart-games":
        return items.filter(item =>
          item.type.includes("heart-games") ||
          item.type === "streak-milestone"
        );
      case "matches":
        return items.filter(item =>
          item.type === "compatibility-suggestion" ||
          item.type === "match-announcement"
        );
      case "explore":
        return items.filter(item =>
          item.type === "community-challenge" ||
          item.type === "leaderboard-update" ||
          item.type === "compatibility-suggestion"
        );
      case "nearby":
        return items.filter(item =>
          item.metadata?.location !== undefined ||
          item.type === "social-activity" ||
          item.type === "profile-update"
        );
      default:
        return items;
    }
  }
}

export const feedService = FeedService;
